import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, TextField, Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import { CalendarMonth, Close, ContentCopy, EventBusy, FileDownload, Folder, PointOfSale, Print, Send, WhatsApp } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, pageHeader, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { dayScheduleService, DaySchedule as DayScheduleData, DayScheduleRow, DayScheduleSummary } from "../services/dayScheduleService";
import { DAY_SCHEDULE_PRINT_KEY, DaySchedulePrintPayload } from "../components/DaySchedulePrint";
import { formatE164Display } from "../utils/phone";
import AppointmentEditorDialog from "../components/AppointmentEditorDialog";
import { useSchedulePermissions } from "../hooks/useSchedulePermissions";
import { scheduleBlocksService, ScheduleBlockItem } from "../services/scheduleBlocksService";

/**
 * Agenda do dia (RN-546): sessões do período com contato de WhatsApp e mensagem pronta. A mensagem
 * padrão da página evita escolher a cada envio; o modal mostra número, mensagem (trocável), texto
 * renderizado, Enviar (wa.me) e Copiar link. Cada clique é registrado para a sessão constar como enviada.
 */
const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fold = (v: string) => v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const fmtTime = (iso: string) => iso.slice(11, 16);
const waLink = (phone: string, text: string) => text ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/${phone}`;

// Cores pelo SIGNIFICADO do status (RN-546a), não pelo nome: "Atendimento completo" da Lumiar é o
// mesmo atendido que "Compareceu" do Ninho
const confirmationChip = (r: DayScheduleRow) => {
  if (r.confirmationKind === "confirmed") return { bg: colors.successSoft, color: "#155724" };
  if (r.confirmationKind === "cancelled") return { bg: colors.errorSoft, color: colors.error };
  return { bg: colors.warningSoft, color: colors.warningSoftText };
};

const presenceChip = (r: DayScheduleRow) => {
  if (r.presenceKind === "attended") return { bg: colors.successSoft, color: "#155724" };
  if (r.presenceKind === "no_show") return { bg: colors.warningSoft, color: colors.warningSoftText };
  return { bg: colors.backgroundAlt, color: colors.textSecondary };
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const weekdayOf = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
};
// Mesmo vermelho claro da agenda (RN-118n): bloqueio se distingue de atendimento sem gritar
const BLOCK_RED = "#e35d6a";

/** Bloqueio com o nome do profissional já resolvido (a Agenda do dia só tem os ids). */
export interface DayScheduleBlock extends ScheduleBlockItem {
  professionalName: string;
}

/** Resumo do período em cards (RN-546a); pacientes de teste ficam fora. */
const SummaryCards: React.FC<{ summary: DayScheduleSummary }> = ({ summary: s }) => {
  const active = s.confirmed + s.pending;
  const pct = active > 0 ? Math.round((s.confirmed / active) * 100) : 0;
  const cancelParts = [
    s.cancelledByPatient > 0 ? `paciente ${s.cancelledByPatient}` : "",
    s.cancelledByProfessional > 0 ? `profissional ${s.cancelledByProfessional}` : "",
    s.cancelledByClinic > 0 ? `clínica ${s.cancelledByClinic}` : "",
  ].filter(Boolean).join(" · ");
  return (
    <Box sx={summaryCardsRowSx} className="c4u-print-block">
      <ReportSummaryCard label="Pacientes" value={String(s.patients)} color={colors.primary} sub={plural(s.sessions, "atendimento", "atendimentos")} note="sem pacientes de teste" />
      <ReportSummaryCard label="Confirmados" value={String(s.confirmed)} color="#155724" sub={`${plural(s.pending, "pendente", "pendentes")} · ${pct}% confirmado`} />
      <ReportSummaryCard label="Cancelados" value={String(s.cancelled)} color={colors.error} sub={cancelParts || "nenhum cancelamento"} />
      <ReportSummaryCard label="Presenças" value={String(s.attended)} color="#155724" sub={`${plural(s.noShow, "falta", "faltas")} · ${s.withoutPresence} sem registro`}
        note={s.withoutPresence > 0 ? "sem registro: sessão que já começou sem presença" : undefined} />
      <ReportSummaryCard label="Pagos" value={String(s.paid)} color="#155724" sub={plural(s.unpaid, "não pago", "não pagos")} note="não pago: presença que cobra, sem baixa" />
    </Box>
  );
};

const DaySchedule: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();

  // Agenda e cadastro abrem em nova aba (pedido do Hilton, 07/09): quem está na recepção não perde a
  // lista do dia. Preserva os parâmetros da URL atual (ex.: clinic) fora de page/id/patient.
  const openInNewTab = (page: string, params?: Record<string, string>) => {
    const urlParams = new URLSearchParams();
    urlParams.set("page", page);
    Object.entries(params ?? {}).forEach(([k, v]) => urlParams.set(k, v));
    new URLSearchParams(window.location.search).forEach((v, k) => {
      if (k !== "page" && k !== "id" && k !== "patient" && !urlParams.has(k)) urlParams.set(k, v);
    });
    window.open(`${window.location.origin}${window.location.pathname}?${urlParams.toString()}`, "_blank", "noopener");
  };
  const { toast, showToast, hideToast } = useToast();
  const clinic = user?.alias || "";
  // Atalho só para quem abre a tela Financeiro do paciente (Administrador e Secretário, RN-543)
  const canOpenFinancial = (user?.allowedPages || []).includes("patient-financial-page");
  const openReceipt = (paymentId: string) => openInNewTab("payment-receipt", { id: paymentId });
  const today = localIso(new Date());

  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [professionalId, setProfessionalId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [search, setSearch] = useState("");
  const [confirmationFilter, setConfirmationFilter] = useState("");
  const [presenceFilter, setPresenceFilter] = useState("");
  // Bloqueios de agenda (RN-118n): marcação, não atendimento — faixa própria, fora da lista e do resumo
  const [blocksFilter, setBlocksFilter] = useState<"show" | "hide">("show");
  const [blocks, setBlocks] = useState<ScheduleBlockItem[]>([]);
  const [data, setData] = useState<DayScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<DayScheduleRow | null>(null);
  const [editing, setEditing] = useState<DayScheduleRow | null>(null);
  const [exporting, setExporting] = useState(false);
  // Clicar na confirmação abre a edição da sessão; só para quem edita agenda
  // RN-546b + RN-115d: o clique abre a edição para quem altera agendamento ou só confirmação/presença
  const canEditSchedule = useSchedulePermissions().canOpenForChange;
  // Abaixo de 768px a lista vira cartão próprio (padrão do sistema)
  const isMobile = useMediaQuery("(max-width:768px)");

  const defaultTemplateApplied = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await dayScheduleService.get({ from, to, professionalId, templateId, clinic });
      setData(d);
      // Só na primeira carga a página assume a mensagem padrão; depois, "Sem mensagem" é escolha válida
      if (!defaultTemplateApplied.current) {
        defaultTemplateApplied.current = true;
        const def = d.templates.find((t) => t.isDefault) ?? d.templates[0];
        if (def) setTemplateId(def.id);
      }
    } catch {
      setError("Não foi possível carregar a agenda do dia.");
    } finally {
      setLoading(false);
    }
  }, [from, to, professionalId, templateId, clinic]);

  useEffect(() => { document.title = "Clinic4Us - Agenda do dia"; }, []);
  useEffect(() => { load(); }, [load]);

  // Bloqueios dos profissionais que a página enxerga (a lista de profissionais já vem escopada)
  const professionalIdsKey = professionalId || (data?.professionals ?? []).map((p) => p.id).join(",");
  useEffect(() => {
    if (!professionalIdsKey) { setBlocks([]); return; }
    let active = true;
    scheduleBlocksService.getBlocksInRange(from, to, professionalIdsKey)
      .then((items) => { if (active) setBlocks(items); })
      .catch(() => { if (active) setBlocks([]); });
    return () => { active = false; };
  }, [from, to, professionalIdsKey]);

  const allRows = useMemo(() => data?.items ?? [], [data]);
  // Opções dos filtros de status: os nomes que aparecem no período (cada clínica tem o seu catálogo)
  // (o escolhido continua na lista quando some do período, senão o seletor fica em branco com o filtro ativo)
  const confirmationOptions = useMemo(() => Array.from(new Set([...allRows.map((r) => r.confirmation || "—"), ...(confirmationFilter ? [confirmationFilter] : [])])).sort(), [allRows, confirmationFilter]);
  const presenceOptions = useMemo(() => Array.from(new Set([...allRows.map((r) => r.presence || "Não informado"), ...(presenceFilter ? [presenceFilter] : [])])).sort(), [allRows, presenceFilter]);
  // Busca e status filtram o que já veio do servidor
  const rows = useMemo(() => {
    const q = fold(search.trim());
    return allRows.filter((r) =>
      (!q || [r.patientName, r.responsibleName, r.professionalName].some((n) => n && fold(n).includes(q)))
      && (!confirmationFilter || (r.confirmation || "—") === confirmationFilter)
      && (!presenceFilter || (r.presence || "Não informado") === presenceFilter));
  }, [allRows, search, confirmationFilter, presenceFilter]);
  // A busca também alcança o bloqueio, pelo profissional ou pelo tipo
  const visibleBlocks = useMemo<DayScheduleBlock[]>(() => {
    if (blocksFilter === "hide") return [];
    const q = fold(search.trim());
    const names = new Map((data?.professionals ?? []).map((p) => [p.id, p.name]));
    return blocks
      .map((b) => ({ ...b, professionalName: names.get(b.professionalRegistrationId) ?? "" }))
      .filter((b) => !q || [b.professionalName, b.title].some((n) => n && fold(n).includes(q)));
  }, [blocks, blocksFilter, search, data]);

  const hasFilters = from !== today || to !== today || professionalId !== "" || search !== "" || confirmationFilter !== "" || presenceFilter !== "" || blocksFilter !== "show";
  const clearFilters = () => { setFrom(today); setTo(today); setProfessionalId(""); setSearch(""); setConfirmationFilter(""); setPresenceFilter(""); setBlocksFilter("show"); };
  const templateName = useMemo(() => data?.templates.find((t) => t.id === templateId)?.name ?? "", [data, templateId]);

  // Impressão em lista, no template da clínica (página própria): os dados vão pela localStorage, porque a
  // sessionStorage não atravessa a aba aberta com noopener
  const printList = () => {
    const prof = data?.professionals.find((p) => p.id === professionalId)?.name;
    const payload: DaySchedulePrintPayload = {
      filtersLabel: `${fmtDate(from)}${to !== from ? ` a ${fmtDate(to)}` : ""}${prof ? ` · ${prof}` : ""}${search.trim() ? ` · busca "${search.trim()}"` : ""}${confirmationFilter ? ` · confirmação ${confirmationFilter}` : ""}${presenceFilter ? ` · presença ${presenceFilter}` : ""}${blocksFilter === "hide" ? " · sem bloqueios" : ""}`,
      rows,
      summary: data?.summary,
      blocks: visibleBlocks,
    };
    try { localStorage.setItem(DAY_SCHEDULE_PRINT_KEY, JSON.stringify(payload)); } catch { /* sem storage: a impressão sai vazia */ }
    window.open(`${window.location.pathname}?page=day-schedule-print`, "_blank", "noopener");
  };

  const exportXlsx = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await dayScheduleService.exportXlsx({ from, to, professionalId });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `agenda_do_dia_${from}_${to}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Não foi possível exportar a planilha.", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    window.location.href = "/?page=login";
  };

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal showCTAButton={false} className="login-header" isLoggedIn={true} notificationCount={0} onRevalidateLogin={handleRevalidateLogin} onLogoClick={goToDashboard} />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Agenda do dia</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Sessões do período com contato de WhatsApp e mensagem pronta. A mensagem escolhida acima da lista vale para todos os envios.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField size="small" label="Buscar por nome" placeholder="Paciente, responsável ou profissional" value={search} onChange={(e) => setSearch(e.target.value)} InputLabelProps={{ shrink: true }} sx={searchFieldSx} />
                <TextField size="small" type="date" label="De" value={from} onChange={(e) => { const v = e.target.value; setFrom(v); if (v && to < v) setTo(v); }} InputLabelProps={{ shrink: true }} sx={filterFieldSx} />
                <TextField size="small" type="date" label="Até" value={to} onChange={(e) => { const v = e.target.value; setTo(v && v < from ? from : v); }} inputProps={{ min: from }} InputLabelProps={{ shrink: true }} sx={filterFieldSx} />
                <TextField select size="small" label="Profissional" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={filterFieldSx}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.professionals ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Confirmação" value={confirmationFilter} onChange={(e) => setConfirmationFilter(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={filterFieldSx}>
                  <MenuItem value="">Todas</MenuItem>
                  {confirmationOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Presença" value={presenceFilter} onChange={(e) => setPresenceFilter(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={filterFieldSx}>
                  <MenuItem value="">Todas</MenuItem>
                  {presenceOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Bloqueios de agenda" value={blocksFilter} onChange={(e) => setBlocksFilter(e.target.value as "show" | "hide")} InputLabelProps={{ shrink: true }} sx={filterFieldSx}>
                  <MenuItem value="show">Mostrar</MenuItem>
                  <MenuItem value="hide">Ocultar</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters || loading} />
                <Tooltip title="Abrir o calendário da agenda">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => openInNewTab("schedule")} aria-label="Abrir a Agenda em nova aba"><CalendarMonth fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Exportar para Excel">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={exportXlsx} disabled={loading || exporting || rows.length === 0} aria-label="Exportar para Excel">
                    {exporting ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : <FileDownload fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Imprimir a lista no modelo da clínica (com telefones)">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={printList} disabled={loading || rows.length === 0} aria-label="Imprimir"><Print fontSize="small" /></IconButton>
                </Tooltip>
              </div>
            </div>

            {error && <Box className="c4u-info-box c4u-info-box--warning" sx={{ mb: 2 }}><Typography>{error}</Typography></Box>}

            {loading || !data ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} sx={{ color: colors.primary }} /></Box>
            ) : (
              <>
                {data.summary && <SummaryCards summary={data.summary} />}
                {/* Configuração do envio, não filtro: fica acima da lista e fora da barra de filtros */}
                <Box className="no-print" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                  <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, flex: { xs: "1 1 100%", sm: "1 1 240px" } }}>
                    {data.templates.length === 0 ? "Nenhuma mensagem cadastrada: Administrativo > Mensagens de WhatsApp." : "A mensagem escolhida vale para todos os envios desta página; no envio dá para trocar só para a sessão."}
                  </Typography>
                  <TextField select size="small" label="Mensagem de WhatsApp para os envios" value={templateId} onChange={(e) => setTemplateId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
                    sx={{ ...filterFieldSx, flex: { xs: "1 1 100%", sm: "0 1 360px" } }}>
                    <MenuItem value="">Sem mensagem</MenuItem>
                    {data.templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}{t.isDefault ? " (padrão)" : ""}</MenuItem>)}
                  </TextField>
                </Box>
                {visibleBlocks.length > 0 && <BlocksBand blocks={visibleBlocks} multiDay={from !== to} />}
                <ResultCount shown={rows.length} total={allRows.length} />
                {isMobile ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {rows.length === 0 && (
                      <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>Nenhuma sessão no período.</Typography>
                    )}
                    {rows.map((r) => {
                      const chip = confirmationChip(r);
                      return (
                        <Box key={r.scheduleId} sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: "10px", p: 1.5, background: "#fff" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                            <Box sx={{ fontWeight: 700, fontSize: "1.05rem" }}>{fmtTime(r.start)} – {fmtTime(r.end)}</Box>
                            <Box sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>{fmtDate(r.start)}</Box>
                          </Box>
                          <Box sx={{ fontWeight: 600, mt: 0.5, overflowWrap: "anywhere" }}>{r.patientName || "—"}</Box>
                          {r.responsibleName && <Box sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>Resp. {r.responsibleName}</Box>}
                          {!r.phone && <Box sx={{ fontSize: "0.8rem", color: colors.error }}>Sem celular no cadastro</Box>}
                          {r.phone && <Box sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>{formatE164Display(r.phone) ?? `+${r.phone}`}</Box>}
                          <Box sx={{ fontSize: "0.85rem", color: colors.textSecondary, mt: 0.5 }}>{[r.specialty, r.professionalName].filter(Boolean).join(" · ") || "—"}</Box>
                          {(r.serviceName || r.planLabel) && <Box sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>{[r.serviceName, r.planLabel].filter(Boolean).join(" · ")}</Box>}
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              <Chip size="small" label={r.confirmation || "—"} onClick={canEditSchedule ? () => setEditing(r) : undefined} aria-label={canEditSchedule ? "Alterar confirmação" : undefined} sx={{ fontWeight: 600, bgcolor: chip.bg, color: chip.color, "&:hover": { bgcolor: chip.bg, filter: "brightness(0.95)" } }} />
                              {r.presence && r.presence !== "Não informado" && <Chip size="small" label={r.presence} onClick={canEditSchedule ? () => setEditing(r) : undefined} aria-label={canEditSchedule ? "Alterar presença" : undefined} sx={{ fontWeight: 600, bgcolor: presenceChip(r).bg, color: presenceChip(r).color, "&:hover": { bgcolor: presenceChip(r).bg, filter: "brightness(0.95)" } }} />}
                              {r.paid && <PaidTag row={r} onOpenReceipt={openReceipt} />}
                              {r.sentCount > 0 && <Chip size="small" label={`WhatsApp ${r.sentCount}×`} sx={{ height: 24, fontSize: "0.7rem", bgcolor: colors.successSoft, color: "#155724", fontWeight: 600 }} />}
                            </Box>
                            <Box sx={{ display: "flex", gap: 0.75 }}>
                              <IconButton size="small" disabled={!r.phone} onClick={() => setTarget(r)} aria-label="WhatsApp" sx={{ bgcolor: "#25D366", color: "white", width: 36, height: 36, "&.Mui-disabled": { bgcolor: colors.backgroundAlt, color: colors.textMuted } }}><WhatsApp fontSize="small" /></IconButton>
                              <IconButton size="small" disabled={!r.patientId} onClick={() => r.patientId && openInNewTab("patient-register", { id: r.patientId })} aria-label="Cadastro em nova aba" sx={{ bgcolor: "#03a9f4", color: "white", width: 36, height: 36 }}><Folder fontSize="small" /></IconButton>
                              {canOpenFinancial && <IconButton size="small" disabled={!r.patientId} onClick={() => r.patientId && openInNewTab("patient-financial-page", { patientId: r.patientId })} aria-label="Financeiro do paciente em nova aba" sx={{ bgcolor: colors.primary, color: "white", width: 36, height: 36 }}><PointOfSale fontSize="small" /></IconButton>}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "2 1 220px", minWidth: 180 }}>Paciente</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 150px" }}>Data/Horário</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "3 1 280px", minWidth: 220 }}>Terapeuta responsável</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 120px", textAlign: "center", "@media (max-width: 1440px)": { display: "none" } }}>Tipo/Plano</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 140px", textAlign: "center" }}>Confirmação</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 130px", textAlign: "center" }}>Presença</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 90px", textAlign: "center" }}>Pago</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 150px", marginLeft: "auto", textAlign: "right" }}>Telefone</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 150px", textAlign: "right" }}>Ações</Box>
                  </Box>
                  {rows.length === 0 && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>Nenhuma sessão no período.</Typography>
                  )}
                  {rows.map((r) => {
                    const chip = confirmationChip(r);
                    return (
                      <Box key={r.scheduleId} className="c4u-table__row">
                        <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "2 1 220px", minWidth: 180 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                            <Box sx={{ fontWeight: 600 }}>{r.patientName || "—"}</Box>
                            {r.responsibleName && <Box sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>Resp. {r.responsibleName}</Box>}
                            {!r.phone && <Box sx={{ fontSize: "0.75rem", color: colors.error }}>Sem celular no cadastro</Box>}
                          </Box>
                        </Box>
                        <Box className="admin-plans-cell" data-label="Data/Horário" sx={{ flex: "0 0 150px" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                            <Box sx={{ fontWeight: 600 }}>{fmtDate(r.start)}</Box>
                            <Box sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>{fmtTime(r.start)}{r.end ? ` - ${fmtTime(r.end)}` : ""}</Box>
                          </Box>
                        </Box>
                        <Box className="admin-plans-cell c4u-col--md" data-label="Terapeuta responsável" sx={{ flex: "3 1 280px", minWidth: 220, fontSize: "0.85rem" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                            <Box>{[r.specialty, r.professionalName].filter(Boolean).join(" · ") || "—"}</Box>
                            {r.serviceName && <Box sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{r.serviceName}</Box>}
                            {/* Até 1440px a coluna Tipo/Plano some (a soma dos mínimos das colunas não cabe): o dado vem para cá */}
                            {r.planLabel && <Box sx={{ display: "none", mt: 0.5, "@media (max-width: 1440px)": { display: "block" } }}><PlanTag label={r.planLabel} /></Box>}
                          </Box>
                        </Box>
                        <Box className="admin-plans-cell c4u-col--xl" data-label="Tipo/Plano" sx={{ flex: "0 0 120px", textAlign: "center", "@media (max-width: 1440px)": { display: "none" } }}>
                          <PlanTag label={r.planLabel} />
                        </Box>
                        <Box className="admin-plans-cell c4u-col--lg" data-label="Confirmação" sx={{ flex: "0 0 140px", textAlign: "center" }}>
                          <Tooltip title={canEditSchedule ? "Alterar confirmação" : ""} arrow>
                            <Chip size="small" label={r.confirmation || "—"} onClick={canEditSchedule ? () => setEditing(r) : undefined} sx={{ fontWeight: 600, bgcolor: chip.bg, color: chip.color, maxWidth: 130, "&:hover": { bgcolor: chip.bg, filter: "brightness(0.95)" } }} />
                          </Tooltip>
                        </Box>
                        <Box className="admin-plans-cell c4u-col--lg" data-label="Presença" sx={{ flex: "0 0 130px", textAlign: "center" }}>
                          <Tooltip title={canEditSchedule ? "Alterar presença" : ""} arrow>
                            <Chip size="small" label={r.presence || "Não informado"} onClick={canEditSchedule ? () => setEditing(r) : undefined} sx={{ fontWeight: 600, maxWidth: 120, bgcolor: presenceChip(r).bg, color: presenceChip(r).color, "&:hover": { bgcolor: presenceChip(r).bg, filter: "brightness(0.95)" } }} />
                          </Tooltip>
                        </Box>
                        <Box className="admin-plans-cell c4u-col--lg" data-label="Pago" sx={{ flex: "0 0 90px", textAlign: "center" }}>
                          {r.paid ? <PaidTag row={r} onOpenReceipt={openReceipt} /> : <Box component="span" sx={{ color: colors.textMuted }}>—</Box>}
                        </Box>
                        <Box className="admin-plans-cell c4u-col--md" data-label="Telefone" sx={{ flex: "0 0 150px", marginLeft: "auto", textAlign: "right", fontSize: "0.85rem", whiteSpace: "nowrap" }}>{r.phone ? (formatE164Display(r.phone) ?? `+${r.phone}`) : <Box component="span" sx={{ color: colors.textMuted }}>—</Box>}</Box>
                        <Box className="admin-plans-cell" data-label="Ações" sx={{ flex: "0 0 150px", display: "flex", gap: 0.5, justifyContent: "flex-end", alignItems: "center" }}>
                          {r.sentCount > 0 && (
                            <Tooltip title={`Enviado ${r.sentCount}× · último por ${r.lastSentBy || "—"} em ${r.lastSentAt ? `${fmtDate(r.lastSentAt)} ${fmtTime(r.lastSentAt)}` : "—"}`} arrow>
                              <Chip size="small" label={`${r.sentCount}×`} sx={{ height: 20, fontSize: "0.7rem", bgcolor: colors.successSoft, color: "#155724", fontWeight: 600 }} />
                            </Tooltip>
                          )}
                          <Tooltip title={r.phone ? "Mensagem de WhatsApp" : "Sem celular no cadastro"}>
                            <span>
                              <IconButton className="c4u-row-action" size="small" disabled={!r.phone} onClick={() => setTarget(r)} sx={{ bgcolor: "#25D366", color: "white", "&:hover": { bgcolor: "#1da851" } }}>
                                <WhatsApp fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Abrir cadastro do paciente">
                            <span>
                              <IconButton className="c4u-row-action" size="small" disabled={!r.patientId} onClick={() => r.patientId && openInNewTab("patient-register", { id: r.patientId })} sx={{ bgcolor: "#03a9f4", color: "white", "&:hover": { bgcolor: "#0288d1" } }}>
                                <Folder fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canOpenFinancial && (
                            <Tooltip title="Financeiro do paciente">
                              <span>
                                <IconButton className="c4u-row-action" size="small" disabled={!r.patientId} onClick={() => r.patientId && openInNewTab("patient-financial-page", { patientId: r.patientId })} sx={{ bgcolor: colors.primary, color: "white", "&:hover": { bgcolor: colors.primaryHover } }}>
                                  <PointOfSale fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <WhatsAppDialog
        row={target}
        templates={data?.templates ?? []}
        pageTemplateId={templateId}
        pageTemplateName={templateName}
        filters={{ from, to, professionalId, clinic }}
        onClose={() => setTarget(null)}
        onRegistered={() => { showToast("Envio registrado.", "success"); load(); }}
      />

      {editing && (
        <AppointmentEditorDialog
          scheduleId={editing.scheduleId}
          patientId={editing.patientId || ""}
          patientName={editing.patientName || ""}
          entityId={user?.entityId || ""}
          onClose={() => setEditing(null)}
          onChanged={load}
        />
      )}

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

/** Faixa dos bloqueios do período (RN-118n): só leitura; editar é na Agenda ou em Bloqueios de agenda. */
const BlocksBand: React.FC<{ blocks: DayScheduleBlock[]; multiDay: boolean }> = ({ blocks, multiDay }) => (
  <Box component="section" aria-label="Bloqueios de agenda do período" sx={{
    mb: 1.5, p: 1.25, borderRadius: "8px", border: `1px dashed ${BLOCK_RED}`, background: "#fdf1f2",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
      <EventBusy sx={{ fontSize: "1.05rem", color: BLOCK_RED }} />
      <Typography sx={{ fontSize: "0.85rem", fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>
        {plural(blocks.length, "bloqueio de agenda", "bloqueios de agenda")}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, display: { xs: "none", sm: "block" } }}>
        · horário marcado como indisponível; não é atendimento e não impede agendar
      </Typography>
    </Box>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {blocks.map((b) => (
        <Tooltip key={b.id} title={b.notes || ""} arrow disableHoverListener={!b.notes} enterTouchDelay={300}>
          <Box sx={{
            display: "inline-flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap",
            px: 1, py: 0.5, borderRadius: "6px", background: "#fff", border: `1px solid ${colors.borderLight}`,
            fontSize: "0.8rem", color: colors.textPrimary, maxWidth: "100%",
          }}>
            <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {multiDay ? `${weekdayOf(b.dateTimeInitial)} ${fmtDate(b.dateTimeInitial).slice(0, 5)} · ` : ""}{fmtTime(b.dateTimeInitial)}–{fmtTime(b.dateTimeFinal)}
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>{b.title}</Box>
            {b.professionalName && <Box component="span" sx={{ color: colors.textSecondary, overflowWrap: "anywhere" }}>{b.professionalName}</Box>}
          </Box>
        </Tooltip>
      ))}
    </Box>
  </Box>
);

/** Tag "Pago": com recibo (pagamento pelo caixa) abre o recibo em nova aba; baixa feita na agenda não tem recibo. */
const PaidTag: React.FC<{ row: DayScheduleRow; onOpenReceipt: (paymentId: string) => void }> = ({ row, onOpenReceipt }) => (
  <Tooltip title={row.paymentId ? "Abrir o recibo em nova aba" : "Baixa feita direto na agenda: não há recibo"} arrow>
    <Chip
      size="small"
      label="Pago"
      onClick={row.paymentId ? () => onOpenReceipt(row.paymentId!) : undefined}
      sx={{ fontWeight: 600, bgcolor: colors.successSoft, color: "#155724", cursor: row.paymentId ? "pointer" : "default", textDecoration: row.paymentId ? "underline" : "none" }}
    />
  </Tooltip>
);

/**
 * Tipo/plano do atendimento (RN-546d). Contornado de propósito: é categoria da sessão, não status —
 * os chips preenchidos ao lado (confirmação, presença, pago) são estado e são clicáveis.
 */
const PlanTag: React.FC<{ label: string | null }> = ({ label }) => {
  if (!label) return <Box component="span" sx={{ color: colors.textMuted }}>—</Box>;
  const isPrivate = label === "Particular";
  return (
    <Tooltip title={label.length > 16 ? label : ""} arrow enterTouchDelay={300}>
      <Chip size="small" variant="outlined" label={label} sx={{
        fontWeight: 600, maxWidth: "100%", bgcolor: "transparent",
        borderColor: isPrivate ? colors.borderLight : colors.primary,
        color: isPrivate ? colors.textPrimary : colors.primaryText,
      }} />
    </Tooltip>
  );
};

// O flex da barra de filtros (1 1 170px) vira ALTURA na coluna do modal e abria vãos entre os campos
const dialogFieldSx = { ...filterFieldSx, flex: "0 0 auto" };

/** Modal de envio: número, mensagem (trocável só para esta sessão), texto, Enviar e Copiar link. */
const WhatsAppDialog: React.FC<{
  row: DayScheduleRow | null;
  templates: { id: string; name: string; isDefault: boolean }[];
  pageTemplateId: string;
  pageTemplateName: string;
  filters: { from: string; to: string; professionalId: string; clinic: string };
  onClose: () => void;
  onRegistered: () => void;
}> = ({ row, templates, pageTemplateId, filters, onClose, onRegistered }) => {
  const [templateId, setTemplateId] = useState(pageTemplateId);
  const [message, setMessage] = useState<string>("");
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTemplateId(pageTemplateId);
    setMessage(pageTemplateId ? row?.message ?? "" : "");
    setError("");
  }, [row, pageTemplateId]);

  // Troca de mensagem só para esta sessão: pede ao servidor o texto renderizado com o outro modelo.
  // Sem modelo escolhido não há texto: a API cai no modelo padrão quando o id vem vazio.
  useEffect(() => {
    if (!templateId) { setMessage(""); return; }
    if (!row || templateId === pageTemplateId) { if (row) setMessage(row.message ?? ""); return; }
    let active = true;
    setLoadingMessage(true);
    dayScheduleService.get({ from: row.start.slice(0, 10), to: row.start.slice(0, 10), templateId, clinic: filters.clinic })
      .then((d) => { if (!active) return; const same = d.items.find((i) => i.scheduleId === row.scheduleId); setMessage(same?.message ?? ""); })
      .catch(() => active && setError("Não foi possível montar a mensagem."))
      .finally(() => active && setLoadingMessage(false));
    return () => { active = false; };
  }, [templateId, row, pageTemplateId, filters.clinic]);

  if (!row || !row.phone) return null;
  const link = waLink(row.phone, message);
  // Sem mensagem só abre a conversa: não é envio do sistema e não conta no "Já enviado"
  const noMessage = !templateId;
  const canAct = !busy && !loadingMessage && (noMessage || !!message);

  const register = async () => {
    if (!row.phone || noMessage) return;
    setBusy(true);
    try {
      await dayScheduleService.registerSend(row.scheduleId, templateId || null, row.phone, message);
      onRegistered();
    } catch {
      setError("O WhatsApp abriu, mas o envio não foi registrado. Tente de novo.");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    window.open(link, "_blank", "noopener");
    await register();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      await register();
    } catch {
      setError("Não foi possível copiar o link.");
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}>
      <DialogTitle sx={{ background: "#25D366", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.25rem 1.5rem", fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><WhatsApp /> Mensagem para {row.responsibleName || row.patientName}</Box>
        <IconButton onClick={onClose} disabled={busy} sx={{ color: "#fff", padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "1.25rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField size="small" label="Número do WhatsApp" value={formatE164Display(row.phone) ?? `+${row.phone}`} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={dialogFieldSx} />
        <TextField select size="small" label="Mensagem" value={templateId} onChange={(e) => setTemplateId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={dialogFieldSx}
          helperText="A escolha vale só para esta sessão; a padrão da página continua a mesma." FormHelperTextProps={{ sx: { mx: 0 } }}>
          <MenuItem value="">Sem mensagem</MenuItem>
          {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}{t.isDefault ? " (padrão)" : ""}</MenuItem>)}
        </TextField>
        <Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.75, color: colors.textSecondary }}>Texto que será enviado</Typography>
          <Box sx={{ background: "#e7ffdb", borderRadius: "10px", p: 1.5, fontSize: "0.9rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere", minHeight: 80, boxShadow: "0 1px 1px rgba(0,0,0,0.08)" }}>
            {loadingMessage ? <CircularProgress size={16} /> : message || <span style={{ color: colors.textMuted }}>{noMessage ? "Sem mensagem: o WhatsApp abre a conversa em branco e o envio não é registrado." : "Esta mensagem não tem texto para esta sessão."}</span>}
          </Box>
          <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, mt: 0.5 }}>
            {row.sentCount > 0 ? `Já enviado ${row.sentCount}× para esta sessão (último por ${row.lastSentBy || "—"}).` : "Ainda não enviado para esta sessão."}
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ padding: "1rem 1.5rem", gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} variant="outlined" disabled={busy} sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary, mr: "auto" }}>Fechar</Button>
        <Button onClick={copy} variant="outlined" disabled={!canAct} startIcon={<ContentCopy />} sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primary }}>Copiar</Button>
        <Button onClick={send} variant="contained" disabled={!canAct} startIcon={busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <Send />}
          sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#25D366", "&:hover": { bgcolor: "#1da851" }, boxShadow: "none" }}>
          {noMessage ? "Abrir conversa" : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DaySchedule;
